import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { IMAGES } from "@/lib/constants";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Library from "@/pages/Library";
import ProductDetail from "@/pages/ProductDetail";
import ContentHub from "@/pages/ContentHub";
import Journal from "@/pages/Journal";
import ArticleDetail from "@/pages/ArticleDetail";
import Shop from "@/pages/Shop";
import FAQ from "@/pages/FAQ";
import Contact from "@/pages/Contact";
import Legal, { PRIVACY, TERMS, REFUND } from "@/pages/Legal";
import ThankYou from "@/pages/ThankYou";
import NotFound from "@/pages/NotFound";
import NewsletterPage from "@/pages/NewsletterPage";
import Membership from "@/pages/Membership";
import Account from "@/pages/Account";
import DailyRecipe from "@/pages/DailyRecipe";
import RecipeDetail from "@/pages/RecipeDetail";
import Store from "@/pages/Store";
import AgnesAI from "@/pages/AgnesAI";
import Admin from "@/pages/Admin";
import FunnelLanding from "@/pages/FunnelLanding";
import MarketingFunnels from "@/pages/MarketingFunnels";
import { AuthProvider } from "@/lib/auth";
import { CartProvider } from "@/lib/cart";
import { CartDrawer } from "@/components/site/CartDrawer";

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppLayout() {
  const { pathname } = useLocation();
  const minimal = pathname.startsWith("/f/");
  return (
    <>
      <ScrollTop />
      {!minimal && <Navbar />}
      <CartDrawer />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/library" element={<Library />} />
          <Route path="/library/:slug" element={<ProductDetail />} />
          <Route path="/pantry-preserving" element={<ContentHub title="Pantry & Preserving" category="Pantry & Preserving" overline="Fill your larder" intro="A full larder is a quiet kind of wealth. Here you'll find everything I know about catching the season in a jar — jams, pickles, chutneys and the gentle art of a well-stocked cottage pantry." image={IMAGES.pantryHub} />} />
          <Route path="/herbal-wisdom" element={<ContentHub title="Herbal Wisdom" category="Herbal Wisdom" overline="Green medicine" intro="There is a remedy for most small troubles growing quietly by the garden wall. Gather here the old, gentle wisdom of herbs — teas, tinctures, salves and the plants of the doorstep." image={IMAGES.herbHub} />} />
          <Route path="/recipes" element={<ContentHub title="Recipes" category="Recipes" overline="From the range" intro="The dishes that have simmered on my range for six decades. Nothing fashionable — only honest food that comforts, gathers a family, and makes an ordinary day feel like a feast." image={IMAGES.breadHub} />} />
          <Route path="/seasonal-living" element={<ContentHub title="Seasonal Living" category="Seasonal Living" overline="With the turning year" intro="We were never meant to live all twelve months the same. Learn to live gently with the seasons — to notice the first snowdrop, the last apple, and the small beauties of each passing month." image={IMAGES.seasonalHub} />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/journal/:slug" element={<ArticleDetail />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/newsletter" element={<NewsletterPage />} />
          <Route path="/membership" element={<Membership />} />
          <Route path="/account" element={<Account />} />
          <Route path="/daily-recipe" element={<DailyRecipe />} />
          <Route path="/daily-recipe/:slug" element={<RecipeDetail />} />
          <Route path="/store" element={<Store />} />
          <Route path="/agnes-ai" element={<AgnesAI />} />
          <Route path="/marketing-funnels" element={<MarketingFunnels />} />
          <Route path="/f/:slug" element={<FunnelLanding />} />
          <Route path="/privacy" element={<Legal {...PRIVACY} />} />
          <Route path="/terms" element={<Legal {...TERMS} />} />
          <Route path="/refund-policy" element={<Legal {...REFUND} />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {!minimal && <Footer />}
      <Toaster position="top-center" />
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen flex flex-col">
      <BrowserRouter>
        <AuthProvider>
        <CartProvider>
          <AppLayout />
        </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
